import { $framework } from "@opendash/core";
import { RouteContext } from "@opendash/router";
import { FormInstance, Upload, UploadFile, UploadProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { makeAutoObservable, runInAction } from "mobx";
import Papa from "papaparse";
import Parse from "parse";
import {
  Agency,
  Bikes_Allowed,
  Calendar,
  Direction,
  Route,
  Stop,
  Stop_Times,
  Trip,
  Wheelchair_Accessible,
  _User,
} from "../js/parsegenerated";

type TableColumnsType = {
  title: string;
  dataIndex: string;
  key: string;
  selectKey: string;
};

type TabelDataType = {
  [key: string]: string;
};

type TemporaryRouteType = {
  key: string;
  name: string;
  disabled: boolean;
};

type TemporaryTripType = {
  key: string;
  name: string;
  disabled: boolean;
};

type RouteFormValueType = {
  route_type: number;
};

type TripFormValueType = {
  bikes_allowed: number;
  wheelchair_accessible: number;
  direction_id: number;
  weekdays: Array<string>;
  start_date: Dayjs;
  end_date: Dayjs;
};

export class ImportPageState {
  ctx: RouteContext;

  currentStep: number = 0;
  importStarted: boolean = false;

  routeForm: FormInstance = null;
  tripForm: FormInstance = null;

  existingRoutes: Route[] = [];
  existingTrips: Trip[] = [];
  existingStops: Stop[] = [];
  directions: Direction[] = [];
  bikesAllowed: Bikes_Allowed[] = [];
  wheelchairAccessible: Wheelchair_Accessible[] = [];

  fileList: UploadFile[] = [];
  tableColumns: TableColumnsType[] = [];
  tableDataSource: TabelDataType[] = [];

  tempRoutes: TemporaryRouteType[] = [];
  tempTrip: TemporaryTripType[] = [];
  tripCount: number = 0;

  selectValues = {
    route: null,
    stop: null,
    longitude: null,
    latitude: null,
    arrival_time1: null,
    departure_time1: null,
  };

  constructor(ctx: RouteContext) {
    this.ctx = ctx;

    ctx.setTitle("gtfs:import.title");
    ctx.setDescription(undefined);

    makeAutoObservable(this);
  }

  async init(agency: Agency) {
    const existingRoutes = await new Parse.Query(Route)
      .equalTo("agency_id", agency)
      .limit(1_000_000)
      .find();

    const existingTrips = await new Parse.Query(Trip)
      .containedIn("route_id", existingRoutes)
      .include("route_id")
      .include("service_id")
      .limit(1_000_000)
      .find();

    const existingStops = await new Parse.Query(Stop).limit(1_000_000).find();

    const directions = await new Parse.Query(Direction)
      .ascending("value")
      .limit(1_000_000)
      .find();

    const bikesAllowed = await new Parse.Query(Bikes_Allowed)
      .ascending("value")
      .limit(1_000_000)
      .find();

    const wheelchairAccessible = await new Parse.Query(Wheelchair_Accessible)
      .ascending("value")
      .limit(1_000_000)
      .find();

    runInAction(() => {
      this.existingRoutes = existingRoutes;
      this.existingTrips = existingTrips;
      this.existingStops = existingStops;
      this.directions = directions;
      this.bikesAllowed = bikesAllowed;
      this.wheelchairAccessible = wheelchairAccessible;
    });
  }

  get selectOptionsMap() {
    return Object.fromEntries([
      ["Nicht beachten", "none"],
      ["Linie", "route"],
      ["Haltestelle", "stop"],
      ["Längengrad", "longitude"],
      ["Breitengrad", "latitude"],

      ...createTripArray(this.tripCount, true).flatMap((i) => [
        ["Ankunftzeit " + i, "arrival_time" + i],
        ["Abfahrtszeit " + i, "departure_time" + i],
      ]),
    ]);
  }

  get selectOptions() {
    return Object.entries(this.selectOptionsMap).map(([label, value]) => ({
      label,
      value,
    }));
  }

  get selectValuesInvers() {
    return Object.fromEntries(
      this.tableColumns.map((col) => [
        col.dataIndex,
        Object.entries(this.selectValues)
          .map(([key, value]) => ({ key, value }))
          .find((i) => i.value === col.dataIndex)?.key || "none",
      ])
    );
  }

  get nextButtonDisabled() {
    if (this.currentStep === 0) {
      return Object.values(this.selectValues).some((v) => v === null);
    }

    return false;
  }

  get backButtonDisabled() {
    if (this.currentStep <= 0) {
      return true;
    }

    return false;
  }

  setRouteForm(form: FormInstance) {
    this.routeForm = form;
  }

  setTripForm(form: FormInstance) {
    this.tripForm = form;
  }

  next(force: boolean = false) {
    this.setCurrentStep(this.currentStep + 1, force);
  }

  back() {
    this.setCurrentStep(this.currentStep - 1);
  }

  async setCurrentStep(step: number, force: boolean = false) {
    if (step === 1) {
      await this.fetchTemporaryRoutes();

      if (this.tempRoutes.every((route) => route.disabled)) {
        $framework.antd_msg.info("Es wurden keine neuen Linien gefunden.");

        step = 2;
      }
    }

    if (!force && step === 2) {
      try {
        await this.routeForm.validateFields();
      } catch (error) {
        return void $framework.antd_msg.error(
          "Bitte geben Sie alle Informationen im Formular an."
        );
      }
    }

    if (step === 2) {
      await this.fetchTemporaryTrips();

      if (this.tempTrip.every((trip) => trip.disabled)) {
        $framework.antd_msg.info("Es wurden keine neuen Fahrten gefunden.");

        step = 3;
      }
    }

    if (!force && step === 3) {
      try {
        await this.tripForm.validateFields();
      } catch (error) {
        return void $framework.antd_msg.error(
          "Bitte geben Sie alle Informationen im Formular an."
        );
      }
    }

    runInAction(() => {
      this.currentStep = step;
    });
  }

  async fetchTemporaryRoutes() {
    const routes: TemporaryRouteType[] = [];

    for (const row of this.tableDataSource) {
      const name = row[this.selectValues.route];

      if (!name) continue;

      const key = createRouteKey(name);

      if (routes.some((route) => route.key === key)) continue;

      const disabled = this.existingRoutes.some((route) => route.key === key);

      routes.push({
        key,
        name,
        disabled,
      });
    }

    runInAction(() => {
      this.tempRoutes = routes;
    });
  }

  async fetchTemporaryTrips() {
    const trips: TemporaryTripType[] = [];

    for (const row of this.tableDataSource) {
      const route_name = row[this.selectValues.route];

      if (!route_name) continue;

      for (const i of createTripArray(this.tripCount)) {
        const key = createTripKey(route_name, i);

        if (!this.selectValues["arrival_time" + i]) continue;
        if (!this.selectValues["departure_time" + i]) continue;
        if (trips.some((trip) => trip.key === key)) continue;

        const disabled = this.existingTrips.some((trip) => trip.key === key);

        trips.push({
          key: key,
          name: route_name + " #" + i,
          disabled,
        });
      }
    }

    runInAction(() => {
      this.tempTrip = trips;
    });
  }

  getUploadProps(): UploadProps {
    return {
      name: "file",
      accept: "text/csv",
      multiple: true,
      maxCount: 1,
      onRemove: (file) => {
        runInAction(() => {
          this.fileList = [];
          this.tableColumns = [];
          this.tableDataSource = [];
        });
      },
      beforeUpload: (file) => {
        const isAllowed = file.type === "text/csv";

        if (!isAllowed) {
          return void $framework.antd_msg.error(`${file.name} is not allowed.`);
        }

        const reader = new FileReader();
        reader.readAsDataURL(file as Blob);
        reader.onload = () => {
          runInAction(() => {
            this.fileList = [
              {
                uid: crypto.randomUUID(),
                url: reader.result as string,
                name: file.name,
                type: file.type,
                size: file.size,
                lastModified: file.lastModified,
                lastModifiedDate: file.lastModifiedDate,
              },
            ];

            this.tableColumns = [];
            this.tableDataSource = [];

            for (const key of Object.keys(this.selectValues)) {
              this.selectValues[key] = null;
            }
          });
        };

        return isAllowed || Upload.LIST_IGNORE;
      },
      customRequest: ({ filename, file, onSuccess, onError }) => {
        this.handleCsvFile(
          new File([file as Blob], filename, {
            type: "text/csv",
          })
        );
      },
    };
  }

  private handleCsvFile(file: File) {
    Papa.parse(file, {
      // header: true,
      delimiter: ";",
      complete: (results, file) => {
        this.handleCsvData(results.data as string[][]);
      },
      error(err, file) {
        $framework.antd_msg.error(
          "Der Inhalt der CSV Datei kann nicht verarbeitet werden."
        );
      },
    });
  }

  private handleCsvData(data: string[][]) {
    const columns = data[0];
    const rows = data.slice(1);

    // Check whether two matching column headers exist
    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];

      const numberofequalcolumns = columns.filter(
        (columntocompare) => column === columntocompare
      );
      if (numberofequalcolumns.length > 1) {
        // Rename the equal columns
        columns[i] = crypto.randomUUID();
        $framework.antd_msg.info(
          `Spalte ${column} umbenannt in ${columns[i]}.`,
          10
        );
      }
    }

    const tableheaders = columns.map((column): TableColumnsType => {
      return {
        title: column,
        dataIndex: column.toLowerCase(),
        key: column.toLowerCase(),
        selectKey: "",
      };
    });

    // Make table content
    const tabledata = rows.map((row, index): TabelDataType => {
      const obj = {
        key: crypto.randomUUID(),
      };

      row.forEach((entry, index) => {
        const entryname = columns[index].toLowerCase();
        obj[entryname] = entry;
      });

      return obj;
    });

    runInAction(() => {
      this.tableColumns = tableheaders;
      this.tableDataSource = tabledata;

      for (const col of this.tableColumns) {
        this.setSelection(col.dataIndex, this.selectOptionsMap[col.title]);
      }
    });
  }

  getSelection(columnIndex: string) {
    return this.selectValuesInvers[columnIndex] || "none";
  }

  setSelection(columnIndex: string, value: string) {
    if (Object.values(this.selectOptionsMap).includes(value)) {
      for (const [eValue, eColumnIndex] of Object.entries(this.selectValues)) {
        if (eColumnIndex === columnIndex) {
          if (
            eValue.startsWith("arrival_time") ||
            eValue.startsWith("departure_time")
          ) {
            delete this.selectValues[eValue];
          } else {
            this.selectValues[eValue] = null;
          }
        }
      }

      this.selectValues[value] = columnIndex;

      // console.log(
      //   columnIndex,
      //   value,
      //   this.selectValues[value],
      //   JSON.stringify(this.selectValues, null, 2),
      //   JSON.stringify(this.selectValuesInvers, null, 2)
      // );

      if (value.startsWith("arrival_time")) {
        const c = parseInt(value.replace("arrival_time", ""));

        if (c > this.tripCount) {
          this.tripCount = c;
        }
      }

      if (value.startsWith("departure_time")) {
        const c = parseInt(value.replace("departure_time", ""));

        if (c > this.tripCount) {
          this.tripCount = c;
        }
      }
    }
  }

  overwriteForAllRoutes(routeKey: string) {
    const oldValues: { [key: string]: RouteFormValueType } =
      this.routeForm.getFieldsValue(true);

    const value = oldValues[routeKey];

    const newValues = Object.fromEntries(
      this.tempRoutes.map((route) => [route.key, value])
    );

    this.routeForm.setFieldsValue(newValues);
  }

  overwriteForAllTrips(routeKey: string) {
    const oldValues: { [key: string]: TripFormValueType } =
      this.tripForm.getFieldsValue(true);

    const value = oldValues[routeKey];

    const newValues = Object.fromEntries(
      this.tempTrip.map((trip) => [trip.key, value])
    );

    console.log(oldValues, newValues);

    this.tripForm.setFieldsValue(newValues);
  }

  overwriteForAllTripsOfSameRoute(routeKey: string) {
    const oldValues: { [key: string]: TripFormValueType } =
      this.tripForm.getFieldsValue(true);

    const value = oldValues[routeKey];

    const newValues = Object.fromEntries(
      this.tempTrip.map((trip) => [trip.key, value])
    );

    this.tripForm.setFieldsValue(newValues);
  }

  async doImport(agency: Agency) {
    try {
      this.importStarted = true;

      const tenant = (Parse.User.current() as _User).get("tenant");

      const routeFormEntries: { [key: string]: RouteFormValueType } =
        this.routeForm.getFieldsValue(true);
      const tripFormEntries: { [key: string]: TripFormValueType } =
        this.tripForm.getFieldsValue(true);

      console.log(routeFormEntries, tripFormEntries);

      const routeColumn = this.selectValues.route as string;
      const stopColumn = this.selectValues.stop as string;
      const latitudeColumn = this.selectValues.latitude as string;
      const longitudeColumn = this.selectValues.longitude as string;

      const rows = this.tableDataSource;

      const routes: Record<string, Route> = {};
      const trips: Record<string, Trip> = {};
      const stops: Record<string, Stop> = {};

      function rowToStopKey(row: TabelDataType) {
        return JSON.stringify([
          row[stopColumn],
          row[latitudeColumn],
          row[longitudeColumn],
        ]);
      }

      const tripStopSequence: Record<string, number> = {};

      const toSave = new Set<Parse.Object>();

      for (const row of rows) {
        const route_name = row[routeColumn];
        const stop_name = row[stopColumn];
        const stop_lat = row[latitudeColumn];
        const stop_lon = row[longitudeColumn];

        if (!route_name || !stop_name || !stop_lat || !stop_lon) continue;

        const routeKey = createRouteKey(route_name);
        const routeFormEntry = routeFormEntries[routeKey];
        const stopKey = rowToStopKey(row);

        if (!stops[stopKey]) {
          stops[stopKey] =
            this.existingStops.find(
              (stop) =>
                stop.stop_name === stop_name &&
                stop.stop_lat === stop_lat &&
                stop.stop_lon === stop_lon
            ) ||
            new Stop({
              tenant,
              stop_id: crypto.randomUUID(),
              stop_name,
              stop_lat,
              stop_lon,
            });
        }

        if (!routes[routeKey]) {
          routes[routeKey] =
            this.existingRoutes.find((route) => route.key === routeKey) ||
            new Route({
              key: routeKey,
              agency_id: agency,
              route_id: crypto.randomUUID(),
              route_short_name: route_name.toLowerCase().trim(),
              route_long_name: route_name,
              route_type: routeFormEntry.route_type,
              tenant: tenant,
            });

          // await routes[routeKey].save();
        }

        const route = routes[routeKey];

        for (const i of createTripArray(this.tripCount)) {
          const tripKey = createTripKey(route_name, i);
          const tripFormEntry = tripFormEntries[tripKey];

          if (!this.selectValues["arrival_time" + i]) continue;
          if (!this.selectValues["departure_time" + i]) continue;

          if (!trips[tripKey]) {
            const existingTrip = this.existingTrips.find(
              (trip) => trip.key === tripKey
            );

            if (existingTrip) {
              trips[tripKey] = existingTrip;

              await Parse.Object.destroyAll(
                await new Parse.Query(Stop_Times)
                  .equalTo("trip_id", existingTrip)
                  .limit(1_000_000)
                  .find()
              );
            } else {
              const calendar = new Calendar({
                start_date: tripFormEntry.start_date
                  ? new Date(tripFormEntry.start_date.toISOString())
                  : new Date(dayjs().toISOString()),
                end_date: tripFormEntry.end_date
                  ? new Date(tripFormEntry.end_date.toISOString())
                  : new Date(dayjs().toISOString()),
                monday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("monday")
                  : undefined,
                tuesday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("tuesday")
                  : undefined,
                wednesday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("wednesday")
                  : undefined,
                thursday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("thursday")
                  : undefined,
                friday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("friday")
                  : undefined,
                saturday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("saturday")
                  : undefined,
                sunday: tripFormEntry.weekdays
                  ? tripFormEntry.weekdays.includes("sunday")
                  : undefined,
                tenant: tenant,
              });

              const trip = new Trip({
                key: tripKey,
                route_id: route,
                service_id: calendar,
                trip_headsign: "",
                trip_short_name: route_name + " #" + i,
                bikes_allowed: this.bikesAllowed.find(
                  (ba) => ba.get("value") === tripFormEntry.bikes_allowed
                ),
                direction_id: this.directions.find(
                  (d) => d.get("value") === tripFormEntry.direction_id
                ),
                wheelchair_accessible: this.wheelchairAccessible.find(
                  (wa) =>
                    wa.get("value") === tripFormEntry.wheelchair_accessible
                ),
                tenant: tenant,
              });

              trips[tripKey] = trip;
            }
          }

          if (!tripStopSequence[tripKey]) {
            tripStopSequence[tripKey] = 0;
          }

          tripStopSequence[tripKey] += 1;

          const stop = stops[stopKey];
          const trip = trips[tripKey];
          const stop_sequence = tripStopSequence[tripKey];

          const arrivalTimeColumn = this.selectValues[
            "arrival_time" + i
          ] as string;

          const departureTimeColumn = this.selectValues[
            "departure_time" + i
          ] as string;

          const arrival_time = row[arrivalTimeColumn];
          const departure_time = row[departureTimeColumn];

          if (!arrival_time || !departure_time) continue;

          if (!trip.route_id.id) {
            await trip.route_id.save();
          }

          if (!trip.service_id.id) {
            await trip.service_id.save();
          }

          if (!trip.id) {
            await trip.save();
          }

          if (!stop.id) {
            await stop.save();
          }

          toSave.add(
            new Stop_Times({
              stop_id: stop,
              trip_id: trip,
              stop_sequence,
              tenant,
              arrival_time: new Date("01.01.1970, " + arrival_time),
              departure_time: new Date("01.01.1970, " + departure_time),
            })
          );
        }
      }

      await Parse.Object.saveAll(Array.from(toSave));

      this.next();
    } catch (e) {
      console.error(e);

      runInAction(() => {
        this.importStarted = false;
      });

      $framework.antd_msg.error(
        "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."
      );
    }
  }
}

function createTripArray(count: number, initial: boolean = false) {
  if (initial) {
    count += 1;
  }
  return Array.from(Array(count).keys()).map((i) => i + 1);
}

function createRouteKey(route_name: string) {
  return route_name.toLowerCase().trim();
}

function createTripKey(route_name: string, i: number) {
  return route_name.toLowerCase().trim() + " #" + i;
}
